<template>
  <div class="mt-4 flex flex-col h-full">
    <!-- change plan dialog -->
    <r-dialog id="update-subscription-dialog" v-model="changePlanDialog.open">
      <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
        <!-- title -->
        <div class="font-bold">
          {{ $t("payments.activeSubscription.dialogs.update.title") }}
        </div>

        <!-- description -->
        <div
          class="text-center mt-8 mb-2"
          v-html="$t('payments.activeSubscription.dialogs.update.description')"
        />

        <!-- select (show current) -->
        <r-select
          id="update-subscription-dropdown"
          v-model="changePlanDialog.selectedPlan"
          class="mb-8"
          :label="$t('payments.activeSubscription.dialogs.update.plans')"
          :options="planOptions"
        />

        <!-- actions -->
        <div class="flex flex-row w-full justify-end mt-10 md:mt-0">
          <!-- cancel -->
          <r-button
            id="update-subscription-cancel"
            class="mr-4"
            text
            color="black"
            @click="toggleChangePlanDialog"
          >
            {{ $t("buttons.cancel") }}
          </r-button>

          <!-- update -->
          <r-button
            id="update-subscription-save"
            :loading="changePlanDialog.loading"
            @click="changePlan"
          >
            {{ $t("buttons.save") }}
          </r-button>
        </div>
      </div>
    </r-dialog>

    <!-- cacnel subscription confirmation dialog -->
    <r-dialog id="cancel-subscription-dialog" v-model="optOutDialog.open">
      <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
        <!-- title -->
        <div class="font-bold">
          {{ $t("payments.activeSubscription.dialogs.delete.title") }}
        </div>

        <!-- content -->
        <div
          class="text-center my-8"
          v-html="$t('payments.activeSubscription.dialogs.delete.description')"
        />

        <!-- actions -->
        <div class="flex flex-row w-full justify-end mt-10 md:mt-0">
          <!-- cancel -->
          <r-button
            id="cancel-subscription-cancel"
            class="mr-4"
            text
            color="black"
            @click="toggleOptOutConfirmation"
          >
            {{ $t("buttons.cancel") }}
          </r-button>

          <!-- delete -->
          <r-button
            id="cancel-subscription-save"
            :loading="optOutDialog.loading"
            color="error"
            @click="cancelSubscription"
          >
            {{ $t("buttons.cancelSubscription") }}
          </r-button>
        </div>
      </div>
    </r-dialog>

    <!-- subscription details -->
    <div class="flex flex-col bg-white rounded mb-6 p-4">
      <div class="flex flex-row items-center mb-2">
        <!-- title -->
        <div class="h-full font-bold">
          {{ $t("payments.activeSubscription.subscriptionInfo.title") }}
        </div>

        <!-- buttons -->
        <div class="flex flex-grow" />

        <!-- edit -->
        <div class="flex md:flex-row flex-col">
          <r-button class="md:mr-2" @click="toggleChangePlanDialog">
            {{ $t("buttons.changePlan") }}
          </r-button>

          <!-- opt-out -->
          <r-button
            class="md:ml-2"
            color="error"
            @click="toggleOptOutConfirmation"
          >
            {{ $t("buttons.cancelSubscription") }}
          </r-button>
        </div>
      </div>

      <!-- row with opt-out button and the subscription type (option to edit subscription) -->
      <div class="flex flex-col" id="subscription-info-card">
        <!-- subscription info -->
        <div>
          <!-- when the plan started -->
          <div>
            <span class="font-semibold">
              {{
                $t("payments.activeSubscription.subscriptionInfo.startDate")
              }}:
            </span>

            {{ toDate((subscriptionInfo || {}).created || "-") }}
          </div>

          <!-- price & period -->
          <div>
            <span class="font-semibold">
              {{ $t("payments.activeSubscription.subscriptionInfo.plan") }}:
            </span>

            <span>
              {{
                $t("payments.activeSubscription.subscriptionInfo.price", {
                  amount: (
                    (((subscriptionInfo || {}).plan || {}).amount || 0) / 100
                  ).toFixed(2),

                  currency: (
                    ((subscriptionInfo || {}).plan || {}).currency || "-"
                  ).toUpperCase(),

                  interval: $t(
                    `globals.${
                      ((subscriptionInfo || {}).plan || {}).interval || "month"
                    }`
                  )
                })
              }}
            </span>
          </div>

          <!-- amount of users -->
          <div>
            <span class="font-semibold">
              {{
                $t(
                  "payments.activeSubscription.subscriptionInfo.amountOfUsers"
                )
              }}:
            </span>

            {{ (subscriptionInfo || {}).quantity || "-" }}
          </div>
        </div>
      </div>
    </div>

    <!-- table of all the recipets and option to download the PDF -->
    <div class="flex h-full bg-white rounded p-4">
      <!-- Loading spinner -->
      <div
        class="flex justify-center content-center w-full"
        v-if="isInvoicesLoading"
      >
        <div>
          <r-loading size="large" />
        </div>
      </div>
      <!-- List of recipets -->
      <r-table
        v-else
        id="invoices-table"
        no-new
        no-search
        :items="invoices"
        :headers="headers"
        :rows-per-page="rowsPerPage"
        empty-items-label="payments.activeSubscription.invoices.emptyItems"
        empty-items-icon="file-slash"
        :label="$t('payments.activeSubscription.invoices.title')"
      >
        <template slot="rows" slot-scope="{ items }">
          <tr v-for="(row, i) in items" :key="i">
            <!-- transaction date -->
            <td>
              {{ toDate(row.created) }}
            </td>

            <!-- amount paid -->
            <td>
              {{ (row.amountPaid / 100).toFixed(2) }}
              {{
                (
                  ((subscriptionInfo || {}).plan || {}).currency || "-"
                ).toUpperCase()
              }}
            </td>

            <!-- download as PDF -->
            <td>
              <div @click="downloadPdf(row.invoicePdf)">
                <r-icon
                  icon="file-download-alt"
                  color="primary"
                  align="right"
                />
              </div>
            </td>
          </tr>
        </template>
      </r-table>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import { mapGetters } from "vuex"
import paymentsAPI from "@/api/payments"

/**
 * Define the payment with active subscription page
 */
export default {
  // component state
  data() {
    return {
      // flag the subscription loading process
      isSubscriptionLoading: true,

      // flag the invoices loading process
      isInvoicesLoading: true,

      // subscription info
      subscriptionInfo: {},

      // invocies
      invoices: [],

      // invoices table headers
      headers: [
        {
          label: "payments.activeSubscription.invoices.created",
          classes: ""
        },
        {
          label: "payments.activeSubscription.invoices.amountPaid",
          classes: ""
        },
        {
          label: "payments.activeSubscription.invoices.invoicePdf",
          classes: "text-right"
        }
      ],

      // change plan dialog state
      changePlanDialog: {
        open: false
      },

      // opt out dialog state
      optOutDialog: {
        open: false
      }
    }
  },

  // external props
  props: {
    // stripe public api key
    apiKey: {
      type: String,
      required: true
    }
  },

  // calculated props
  computed: {
    // spread store values
    ...mapGetters({
      jwt: "auth/getIdToken"
    }),

    /**
     * Returns rows per page
     */
    rowsPerPage() {
      console.log("Screen height: ", window.screen.height)
      if (window.screen.height < 900) {
        return 8
      } else if (window.screen.height >= 900 && window.screen.height < 1080) {
        return 10
      } else return 10
    },

    /**
     * pricing plan options
     */
    planOptions() {
      return [
        { display: this.$t("payments.yearly"), value: "yearly" },
        { display: this.$t("payments.monthly"), value: "monthly" }
      ]
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // load subscription data
  beforeMount() {
    // load subscription info
    this.loadSubscription()

    // load recipets
    this.loadInvoices()
  },

  // component methods
  methods: {
    /**
     * Load the subscription information
     */
    async loadSubscription() {
      try {
        const subscriptionInfo = await paymentsAPI
          .subscriptions(this.jwt)
          .getSubscriptionInfo()

        console.log("got subsciription info", subscriptionInfo)
        this.subscriptionInfo = subscriptionInfo?.subscription || {}
      } catch (e) {
        console.error("unable to load subscription info", e)
      }

      this.isSubscriptionLoading = false
    },

    /**
     * Load the invoices
     */
    async loadInvoices() {
      try {
        const invoices = await paymentsAPI
          .subscriptions(this.jwt)
          .listInvoices()

        console.log("got invoices info", invoices)
        this.invoices = (invoices?.invoicesList || []).sort(
          (a, b) => b.created - a.created
        )
      } catch (e) {
        console.error("unable to load subscription info", e)
      }

      this.isInvoicesLoading = false
    },

    /**
     * Toggle the change plan dialog
     */
    toggleChangePlanDialog() {
      if (this.changePlanDialog.open) {
        this.changePlanDialog = {
          open: false,
          loading: false
        }
      } else {
        this.changePlanDialog = {
          currentPlan: this.subscriptionInfo?.plan?.interval + "ly",
          selectedPlan: this.subscriptionInfo?.plan?.interval + "ly",
          open: true
        }
      }
    },

    /**
     * Toggle the cancel subscription confirmation dialog
     */
    toggleOptOutConfirmation() {
      if (this.optOutDialog.open) {
        this.$set(this.optOutDialog, "open", false)
        this.$set(this.optOutDialog, "loading", false)
      } else {
        this.$set(this.optOutDialog, "open", true)
      }
    },

    /**
     * Change the subscription plan
     */
    async changePlan() {
      this.$set(this.changePlanDialog, "loading", true)

      // check that there is a reason to change the plan
      if (
        this.changePlanDialog.currentPlan !== this.changePlanDialog.selectedPlan
      ) {
        try {
          await paymentsAPI
            .subscriptions(this.jwt)
            .changeSubscriptionPlan(
              this.changePlanDialog.selectedPlan.slice(0, -2)
            )

          await this.loadSubscription()
        } catch (e) {
          console.error("unable to change plan", e)
          this.notifyError({
            title: this.$t("errors.unableToChangeSubscription"),
            message: e.message
          })
        }
      }

      this.toggleChangePlanDialog()
    },

    /**
     * Cancel the subscription
     */
    async cancelSubscription() {
      this.$set(this.optOutDialog, "loading", true)

      // cancel subscription
      try {
        await paymentsAPI.subscriptions(this.jwt).cancelSubscription()
      } catch (e) {
        console.error("unable to cancel subscription", e)
        this.notifyError({
          title: this.$t("errors.unableToCancelSubscription"),
          message: e.message
        })
      }

      this.toggleOptOutConfirmation()
    },

    /**
     * Opens the PDF link and download it
     */
    downloadPdf(link) {
      window.open(link, "_blank")
    },

    /**
     * Converts unix timestamp to date
     */
    toDate(unixTs) {
      return moment.unix(unixTs).format("DD/MM/YYYY")
    }
  }
}
</script>
